/**
 * Form requirements ("hooks") — which forms are required for which employees / employment-relationships.
 * Mirrors the backend CheqMe.Server.Services.Forms.Abstractions/Requirements.
 */

/** Who a requirement is hooked to. (Company-wide-all is intentionally not in v1.) */
export enum RequirementTargetType {
  /** Required for one specific employee. */
  Employee = 0,
  /** Required for everyone in an employment-relationship (job class) — current and future members. */
  EmploymentRelationship = 1
}

/**
 * What happens to an employee who does not yet have a submission for a newly assigned form. Mirrors the backend
 * `CheqMe.Server.Services.Forms.Abstractions/Requirements/BackfillPolicy` (serialised as its numeric value).
 *
 * `Immediate` is **0** deliberately: it is the pre-existing behaviour and therefore the value every stored row
 * defaults to — a requirement can never silently gain a grace window.
 *
 * Since #504 a requirement carries **two** of these, one per cohort — see {@link FormRequirementResource}.
 */
export enum BackfillPolicy {
  /** Locked out (verdict `Restricted`) at the next login until the form is submitted. */
  Immediate = 0,
  /**
   * Kept working for the requirement's grace window (verdict `ReviewDue` — full access, warned + time-boxed);
   * `Restricted` only once that window has passed. Where the window is measured *from* depends on the cohort:
   * the requirement's effective-from date for existing staff, the employee's own start for new staff.
   */
  Grace = 1
}

/** Default window length in days — the fixed period this replaced (backend `RequirementGraceDays.Default`). */
export const GRACE_DAYS_DEFAULT = 14;

/**
 * Shortest window the server accepts. Zero is **not** offered on purpose: the backend answers 400 for it
 * ("a Grace policy granting no grace is an Immediate one wearing the wrong label"), so a UI that let a manager
 * type it would trade a clear choice for a failed request.
 */
export const GRACE_DAYS_MIN = 1;

/** Longest window the server accepts — ten years, a guard against a typo'd deadline that never arrives. */
export const GRACE_DAYS_MAX = 3650;

/**
 * Whether `days` is a window length the server will accept. Mirrors `RequirementGraceDays.IsValid`, plus the
 * integer check the wire type implies — the API's `int?` cannot carry `14.5`.
 */
export function isValidGraceDays(days: number | null | undefined): days is number {
  return typeof days === 'number' && Number.isInteger(days)
    && days >= GRACE_DAYS_MIN && days <= GRACE_DAYS_MAX;
}

/**
 * German label for a policy, as shown in both cohort dropdowns.
 *
 * Deliberately carries **no** duration any more: the window length is its own field now, and a label reading
 * "Frist (14 Tage)" beside a `graceDays` of 30 would be a lie the manager could not correct.
 */
export function backfillPolicyLabel(policy: BackfillPolicy): string {
  return policy === BackfillPolicy.Grace ? 'Frist gewähren' : 'Sofort sperren';
}

/**
 * What the policy means for staff employed **without interruption** since before the assignment took effect.
 *
 * Their window runs from the requirement's own effective-from date, so it is **one shared date** for all of
 * them — which is why this wording may name a deadline and {@link newStaffPolicyHint}'s may not.
 */
export function existingStaffPolicyHint(policy: BackfillPolicy, graceDays: number): string {
  return policy === BackfillPolicy.Grace
    ? `Diese Mitarbeiter arbeiten noch ${graceDays} Tage weiter, bis zu einem gemeinsamen Stichtag.`
    : 'Diese Mitarbeiter werden ab der nächsten Anmeldung gesperrt, bis das Formular vorliegt.';
}

/**
 * What the policy means for everyone whose **current** employment began at or after that point — a new hire
 * *and* a returning one.
 *
 * The anchor is the start of the current stint, so an employee who left and came back falls here, not in the
 * existing-staff half. Their window starts when *they* start, so there is no shared date to name — and naming
 * one would be wrong for every person it was shown to. The wording is therefore always relative ("ab Eintritt"),
 * never a calendar date.
 */
export function newStaffPolicyHint(policy: BackfillPolicy, graceDays: number): string {
  return policy === BackfillPolicy.Grace
    ? `Jede dieser Personen hat ${graceDays} Tage ab dem eigenen Eintritt — es gibt kein gemeinsames Datum.`
    : 'Diese Mitarbeiter werden ab der ersten Anmeldung nach ihrem Eintritt gesperrt, bis das Formular vorliegt.';
}

// There is deliberately no `backfillPolicyClass` here any more (#501). The assignment list stopped rendering a
// policy as a coloured chip: a policy is a setting with a value, and a pill gave the least important part of the
// row the most weight. The one distinction still worth colour — a lockout against a granted window — lives in
// the text colour at the single place that draws it, and did not earn a shared helper.

/** A form requirement as returned to a manager. */
export interface FormRequirementResource {
  id: string;
  formKey: string;
  targetType: RequirementTargetType;
  employeeId?: string | null;
  employmentRelationshipId?: string | null;
  start?: string | null;
  end?: string | null;
  mandatory: boolean;
  /**
   * How the assignment bites staff employed **without interruption** since before it took effect. Optional here
   * only to survive a server that predates #504; read it through the component's accessor, which defaults to
   * {@link BackfillPolicy.Immediate}.
   */
  existingStaffPolicy?: BackfillPolicy;
  /**
   * How it bites everyone whose **current** employment began at or after that point — new hires and returning
   * employees alike, since the anchor is the start of the current stint. Same default, same reason.
   */
  newStaffPolicy?: BackfillPolicy;
  /** Window length in days for whichever cohort was given a {@link BackfillPolicy.Grace} policy. */
  graceDays?: number;
  /**
   * The end of the backfill window for the **existing-staff** cohort — the very date those employees are warned
   * about, so a manager and an employee never read two different deadlines. Null when that cohort's policy is
   * `Immediate`.
   *
   * There is deliberately **no new-staff counterpart**: that window is per person and only known at their login.
   */
  existingStaffDeadline?: string | null;
}

/** Create a form requirement: hook a form to a target (employee or employment-relationship). */
export interface CreateFormRequirementRequest {
  formKey: string;
  targetType: RequirementTargetType;
  /** The target id — an employee id or an employment-relationship id, per {@link targetType}. */
  targetId: string;
  start?: string | null;
  end?: string | null;
  mandatory: boolean;
  /**
   * Always sent, both of them. The server only falls back to its retired single-policy field when **neither**
   * cohort field is present, and that fallback silently denies new hires any window at all — so an explicit
   * pair is the difference between "what the manager chose" and "what the compatibility shim guessed".
   */
  existingStaffPolicy: BackfillPolicy;
  newStaffPolicy: BackfillPolicy;
  /** Window length in days, `1..3650`. Always sent so the server never has to guess. */
  graceDays: number;
}

/**
 * Change an existing assignment's gating settings.
 *
 * Deliberately narrow — the form key and the target are not editable, because re-pointing either would move the
 * effective-from date every deadline hangs off. Re-targeting is delete plus create.
 *
 * `start`/`end` are part of the request even though this screen does not edit them: the server **assigns** them
 * unconditionally, so leaving them out would null the assignment's temporal validity as a side effect of
 * changing a policy.
 */
export interface UpdateFormRequirementRequest {
  start?: string | null;
  end?: string | null;
  mandatory: boolean;
  existingStaffPolicy: BackfillPolicy;
  newStaffPolicy: BackfillPolicy;
  graceDays: number;
}

/**
 * The three gating settings while a manager is editing them — shared by the "new assignment" card and the
 * in-place row editor, so both render from one definition and one set of validation rules.
 *
 * `graceDays` is nullable **here only**: a number input reports an empty field as `null`, and the difference
 * between "not filled in yet" and a real value is what the invalid state is drawn from. It is never sent as null.
 */
export interface RequirementGatingDraft {
  existingStaffPolicy: BackfillPolicy;
  newStaffPolicy: BackfillPolicy;
  graceDays: number | null;
}

/** A fresh draft with the server's own defaults — both cohorts `Immediate`, the 14-day window. */
export function defaultGatingDraft(): RequirementGatingDraft {
  return {
    existingStaffPolicy: BackfillPolicy.Immediate,
    newStaffPolicy: BackfillPolicy.Immediate,
    graceDays: GRACE_DAYS_DEFAULT
  };
}

/** The draft a manager starts from when they open an existing assignment for editing. */
export function gatingDraftOf(requirement: FormRequirementResource): RequirementGatingDraft {
  return {
    existingStaffPolicy: requirement.existingStaffPolicy ?? BackfillPolicy.Immediate,
    newStaffPolicy: requirement.newStaffPolicy ?? BackfillPolicy.Immediate,
    graceDays: requirement.graceDays ?? GRACE_DAYS_DEFAULT
  };
}
